/**
 * DebtAccount Data Model
 * Tracks financial obligations associated with clients
 */

// DebtAccount field validation rules
export const DEBT_ACCOUNT_VALIDATION_RULES = {
  debtAmount: {
    required: true,
    min: 0,
    precision: 2
  },
  adjustments: {
    required: false,
    precision: 2
  },
  legalFees: {
    required: false,
    min: 0,
    precision: 2
  },
  interest: {
    required: false,
    min: 0,
    precision: 2
  },
  debtFees: {
    required: false,
    min: 0,
    precision: 2
  },
  payments: {
    required: false,
    min: 0,
    precision: 2
  }
};

/**
 * Creates a new DebtAccount object with default values
 * @param {Object} data - Initial debt account data
 * @returns {Object} DebtAccount object
 */
export const createDebtAccount = (data = {}) => {
  const debtAmount = parseFloat(data.debtAmount) || 0;
  const adjustments = parseFloat(data.adjustments) || 0;
  const legalFees = parseFloat(data.legalFees) || 0;
  const interest = parseFloat(data.interest) || 0;
  const debtFees = parseFloat(data.debtFees) || 0;
  const payments = parseFloat(data.payments) || 0;
  
  return {
    id: data.id || generateDebtAccountId(),
    clientId: data.clientId || '',
    debtAmount: roundToTwoDecimals(debtAmount),
    adjustments: roundToTwoDecimals(adjustments),
    legalFees: roundToTwoDecimals(legalFees),
    interest: roundToTwoDecimals(interest),
    debtFees: roundToTwoDecimals(debtFees),
    payments: roundToTwoDecimals(payments),
    balance: calculateBalance(debtAmount, adjustments, legalFees, interest, debtFees, payments),
    lastPaymentDate: data.lastPaymentDate || null,
    lastPaymentAmount: roundToTwoDecimals(parseFloat(data.lastPaymentAmount) || 0),
    createdAt: data.createdAt || new Date(),
    updatedAt: data.updatedAt || new Date()
  };
};

/**
 * Calculates the current balance for a debt account
 * Formula: (debt amount + adjustments + legal fees + interest + debt fees - payments)
 * @param {number} debtAmount - Original debt amount
 * @param {number} adjustments - Account adjustments (can be negative)
 * @param {number} legalFees - Legal fees
 * @param {number} interest - Interest charges
 * @param {number} debtFees - Additional debt fees
 * @param {number} payments - Total payments made
 * @returns {number} Calculated balance rounded to 2 decimal places
 */
export const calculateBalance = (debtAmount, adjustments, legalFees, interest, debtFees, payments) => {
  const balance = debtAmount + adjustments + legalFees + interest + debtFees - payments;
  return roundToTwoDecimals(balance);
};

/**
 * Updates a debt account with new payment information
 * @param {Object} debtAccount - Existing debt account
 * @param {number} paymentAmount - Payment amount
 * @param {Date} paymentDate - Payment date
 * @returns {Object} Updated debt account
 */
export const recordPayment = (debtAccount, paymentAmount, paymentDate = new Date()) => {
  const amount = roundToTwoDecimals(parseFloat(paymentAmount) || 0);
  const newPayments = roundToTwoDecimals(debtAccount.payments + amount);
  
  return {
    ...debtAccount,
    payments: newPayments,
    balance: calculateBalance(
      debtAccount.debtAmount,
      debtAccount.adjustments,
      debtAccount.legalFees,
      debtAccount.interest,
      debtAccount.debtFees,
      newPayments
    ),
    lastPaymentDate: paymentDate,
    lastPaymentAmount: amount,
    updatedAt: new Date()
  };
};

/**
 * Validates a debt account object
 * @param {Object} debtAccount - DebtAccount object to validate
 * @returns {Object} Validation result with isValid boolean and errors array
 */
export const validateDebtAccount = (debtAccount) => {
  const errors = [];
  
  // Check required fields
  if (!debtAccount.clientId || debtAccount.clientId.trim().length === 0) {
    errors.push('Client ID is required');
  }
  
  if (debtAccount.debtAmount === undefined || debtAccount.debtAmount === null) {
    errors.push('Debt amount is required');
  }
  
  // Validate numeric fields
  const numericFields = ['debtAmount', 'adjustments', 'legalFees', 'interest', 'debtFees', 'payments'];
  numericFields.forEach(field => {
    const value = debtAccount[field];
    if (value !== undefined && value !== null) {
      if (isNaN(value)) {
        errors.push(`${field} must be a valid number`);
      } else if (field !== 'adjustments' && value < 0) {
        // Adjustments can be negative, others cannot
        errors.push(`${field} cannot be negative`);
      }
    }
  });
  
  // Validate precision (no more than 2 decimal places)
  numericFields.forEach(field => {
    const value = debtAccount[field];
    if (value !== undefined && value !== null && !isNaN(value)) {
      const decimalPlaces = (value.toString().split('.')[1] || '').length;
      if (decimalPlaces > 2) {
        errors.push(`${field} cannot have more than 2 decimal places`);
      }
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Rounds a number to 2 decimal places
 * @param {number} value - Number to round
 * @returns {number} Rounded number
 */
export const roundToTwoDecimals = (value) => {
  return Math.round((value + Number.EPSILON) * 100) / 100;
};

/**
 * Generates a unique debt account ID
 * @returns {string} Unique debt account ID
 */
const generateDebtAccountId = () => {
  return 'debt_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

/**
 * Formats a monetary value for display
 * @param {number} value - Monetary value
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value || 0);
};